<template>
  <div class="about-container">
    <div class="head">
      <h1>{{ t("home.aboutus.title") }}</h1>
      <div class="head-text">{{ t("home.aboutus.headertext") }}</div>
    </div>
    <div class="main-wrapper">
      <aside class="sidebar">
        <div class="nav-container">
          <div
            v-for="(item, index) in navList"
            :key="index"
            class="nav-item-wrapper"
          >
            <div class="nav-item" @click="activeIndex = index">
              <div
                class="nav-line"
                :class="{ active: activeIndex === index }"
              ></div>
              <span class="nav-text" :class="{ active: activeIndex === index }">
                {{ t(`home.aboutus.navItems.${item.key}.label`) }}
              </span>
            </div>

            <div v-if="index < navList.length - 1" class="nav-divider">
              <div class="divider-line darker"></div>
              <div class="divider-line lighter"></div>
            </div>
          </div>
        </div>
      </aside>
      <main class="content">
        <!-- 图片区域 -->
        <div class="content-img" v-if="activeIndex !== 4">
          <img :src="currentImage" alt="" />
        </div>

        <!-- 轮播图区域 (仅康码动态与风采显示) -->
        <div class="carousel-container" v-if="activeIndex === 4">
          <div
            class="carousel"
            @touchstart="handleTouchStart"
            @touchend="handleTouchEnd"
          >
            <div
              class="carousel-slide"
              v-for="(slide, index) in carouselSlides"
              :key="index"
              :class="{ active: currentSlide === index }"
            >
              <img :src="slide.image" :alt="slide.alt" />
              <div class="slide-caption">
                <h3>
                  {{ t(`home.aboutus.carousel.slide${index + 1}.title`) }}
                </h3>
                <p>
                  {{ t(`home.aboutus.carousel.slide${index + 1}.description`) }}
                </p>
              </div>
            </div>
          </div>

          <div class="carousel-controls">
            <div class="carousel-indicators">
              <span
                v-for="(slide, index) in carouselSlides"
                :key="index"
                :class="{ active: currentSlide === index }"
                @click="currentSlide = index"
              ></span>
            </div>
          </div>
        </div>

        <div class="content-text">
          <h2>{{ currentTitle }}</h2>
          <div class="content-paragraphs">
            <p v-for="(paragraph, index) in paragraphContent" :key="index">
              {{ paragraph }}
            </p>
          </div>
        </div>
      </main>
    </div>

    <!-- 移动端卡片布局 -->
    <div class="mobile-cards">
      <div v-for="(item, index) in navList" :key="index" class="mobile-card">
        <div class="card-img" v-if="index !== 4">
          <img :src="navImages[index]" alt="" />
        </div>
        <div
          class="mobile-carousel"
          v-if="index === 4"
          @touchstart="handleTouchStart"
          @touchend="handleTouchEnd"
        >
          <div class="carousel-slide active">
            <img
              :src="carouselSlides[currentSlide].image"
              :alt="carouselSlides[currentSlide].alt"
            />
            <div class="slide-caption">
              <h3>
                {{ t(`home.aboutus.carousel.slide${currentSlide + 1}.title`) }}
              </h3>
              <p>
                {{
                  t(
                    `home.aboutus.carousel.slide${currentSlide + 1}.description`
                  )
                }}
              </p>
            </div>
          </div>
          <div class="mobile-carousel-controls">
            <div class="carousel-indicators">
              <span
                v-for="(slide, idx) in carouselSlides"
                :key="idx"
                :class="{ active: currentSlide === idx }"
                @click.stop="currentSlide = idx"
              ></span>
            </div>
          </div>
        </div>
        <div class="card-header">
          <h3 class="card-title">
            {{ t(`home.aboutus.navItems.${item.key}.label`) }}
          </h3>
        </div>

        <div class="card-content">
          <!-- 轮播图区域 (仅康码动态与风采显示) -->

          <div class="card-text">
            <div :class="{ 'text-collapsed': !mobileTextExpanded[index] }">
              <p
                v-for="(paragraph, pIndex) in getMobileParagraphContent(index)"
                :key="pIndex"
                :class="{
                  'collapsed-hidden': !mobileTextExpanded[index] && pIndex >= 3,
                }"
              >
                {{ paragraph }}
              </p>
            </div>
            <button
              v-if="shouldShowMobileToggle(index)"
              class="toggle-btn"
              @click.stop="toggleMobileText(index)"
            >
              {{
                mobileTextExpanded[index]
                  ? t("home.aboutus.liter")
                  : t("home.aboutus.more")
              }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from "vue";
import { useI18n } from "vue-i18n";
import aboutimg1 from "../../assets/image/home/about-1.webp";
import aboutimg2 from "../../assets/image/home/about-2.webp";
import aboutimg3 from "../../assets/image/home/about-3.webp";
import aboutimg4 from "../../assets/image/home/about-4.webp";
import rzximg3 from "../../assets/image/home/renzx3.webp";
const { t } = useI18n();

const navList = ref([
  { key: "technology" },
  { key: "advantage" },
  { key: "product" },
  { key: "mission" },
  { key: "news" },
]);

const activeIndex = ref(0);

// 移动端文字展开状态
const mobileTextExpanded = ref({});

// 轮播图相关
const currentSlide = ref(0);
const carouselSlides = ref([
  {
    image:
      "https://a.storyblok.com/f/287055582545350/1392x1392/3df59cdeb7/about-5a.png/m/filters:format(webp):quality(85)",
    alt: "News 1",
  },
  {
    image:
      "https://a.storyblok.com/f/287055582545350/1600x1600/867b527b9a/about-5b.png/m/filters:format(webp):quality(85)",
    alt: "News 2",
  },
  {
    image:
      "https://a.storyblok.com/f/287055582545350/1600x1600/26d5b69f24/about-5c.png/m/filters:format(webp):quality(85)",
    alt: "News 3",
  },
  {
    image:
      "https://a.storyblok.com/f/287055582545350/1600x1600/eef70b3a41/about-5d.png/m/filters:format(webp):quality(85)",
    alt: "News 4",
  },
  {
    image:
      "https://a.storyblok.com/f/287055582545350/1600x1600/042de54fe1/about-5e.png/m/filters:format(webp):quality(85)",
    alt: "News 5",
  },
]);

const navImages = ref([aboutimg1, aboutimg2, aboutimg3, aboutimg4]);
const touchStartX = ref(0);
const touchEndX = ref(0);

const handleTouchStart = (e) => {
  touchStartX.value = e.changedTouches[0].screenX;
};

const handleTouchEnd = (e) => {
  touchEndX.value = e.changedTouches[0].screenX;
  handleSwipeGesture();
};

const handleSwipeGesture = () => {
  const swipeThreshold = 50;
  const diff = touchStartX.value - touchEndX.value;

  if (Math.abs(diff) > swipeThreshold) {
    if (diff > 0) {
      nextSlide();
    } else {
      prevSlide();
    }
  }
};
// 移动端方法
const getMobileContent = (index) => {
  if (index === 4) {
    return t(
      `home.aboutus.carousel.slide${currentSlide.value + 1}.description`
    );
  } else {
    return t(`home.aboutus.navItems.${navList.value[index].key}.content`);
  }
};
// 将文本内容按段落分割的计算属性
const paragraphContent = computed(() => {
  const content = currentContent.value;
  if (activeIndex.value === 4) {
    return [content];
  } else {
    return content.split("\n").filter((paragraph) => paragraph.trim() !== "");
  }
});

// 移动端段落分割处理
const getMobileParagraphContent = (index) => {
  const content = getMobileContent(index);
  if (index === 4) {
    return [content];
  } else {
    // 对于其他项，按换行符分割成段落
    return content.split("\n").filter((paragraph) => paragraph.trim() !== "");
  }
};
// 获取格式化后的移动端内容（处理换行）
const getMobileFormattedContent = (index) => {
  const content = getMobileContent(index);
  if (index === 4) {
    // 对于新闻项，直接返回描述文字
    return content;
  } else {
    // 对于其他项，处理换行
    return content.replace(/\n/g, "<br>");
  }
};

const toggleMobileText = (index) => {
  mobileTextExpanded.value[index] = !mobileTextExpanded.value[index];
};

const shouldShowMobileToggle = (index) => {
  const content = getMobileContent(index);
  // 根据内容长度判断是否需要显示切换按钮
  return content.length > 100; 
};

// 计算当前显示的图片
const currentImage = computed(() => {
  return navImages.value[activeIndex.value];
});

// 计算当前显示的标题
const currentTitle = computed(() => {
  if (activeIndex.value === 4) {
    // 新闻项
    return t(`home.aboutus.carousel.slide${currentSlide.value + 1}.title`);
  } else {
    return t(
      `home.aboutus.navItems.${navList.value[activeIndex.value].key}.title`
    );
  }
});

// 计算当前显示的内容
const currentContent = computed(() => {
  if (activeIndex.value === 4) {
    return t(
      `home.aboutus.carousel.slide${currentSlide.value + 1}.description`
    );
  } else {
    return t(
      `home.aboutus.navItems.${navList.value[activeIndex.value].key}.content`
    );
  }
});

// 格式化内容以处理换行
const formattedContent = computed(() => {
  return currentContent.value;
});

// 轮播图自动播放
let carouselInterval;

const nextSlide = () => {
  currentSlide.value = (currentSlide.value + 1) % carouselSlides.value.length;
};

const prevSlide = () => {
  currentSlide.value =
    currentSlide.value === 0
      ? carouselSlides.value.length - 1
      : currentSlide.value - 1;
};

// 当切换到新闻项时，重置轮播图为第一张
watch(activeIndex, (newIndex) => {
  if (newIndex === 4) {
    currentSlide.value = 0;
  }
});

// 监听当前轮播图变化，更新内容
watch(currentSlide, () => {
  // 当轮播图切换时，内容会自动更新（因为currentContent是计算属性）
});

onMounted(() => {
  // 启动轮播图自动播放
  carouselInterval = setInterval(nextSlide, 5000);
});

onUnmounted(() => {
  // 清除轮播图定时器
  if (carouselInterval) {
    clearInterval(carouselInterval);
  }
});
</script>

<style scoped>
/* 整体容器布局 */
.about-container {
  display: flex;
  width: 100%;
  background-color: #fff;
  padding: 6rem;
  box-sizing: border-box;
  flex-direction: column;
}

.about-container .head {
  display: flex;
  align-items: flex-start;
  flex-direction: row;
  justify-content: space-between;
  margin: 4rem 0;
}

.about-container .head h1 {
  font-size: 3.8rem;
  font-weight: 400;
  letter-spacing: normal;
  line-height: 1;
  text-transform: none;
  width: 60%;
}

.head-text {
  color: #333;
  font-size: 1.4rem;
  width: 65%;
}

.main-wrapper {
  display: flex;
  flex: 1;
  flex-direction: row;
  overflow: hidden;
}

/* 左侧导航栏 */
.sidebar {
  width: 21.5rem;
  padding: 2.5rem 1.25rem;
  display: flex;
  flex-direction: column;
  position: relative;
}

.content-text .content-paragraphs p {
  margin-bottom: 1.2rem;
  line-height: 1.6;
}

.content-text .content-paragraphs p:last-child {
  margin-bottom: 0;
}

/* 移动端段落样式 */
.card-text p {
  margin-bottom: 0.8rem;
  font-size: 1.4rem;
  line-height: 1.6;
  color: #333;
}

.card-text p:last-child {
  margin-bottom: 0;
}

.collapsed-hidden {
  display: none;
}

/* 导航容器 */
.nav-container {
  display: flex;
  flex-direction: column;
}

/* 导航项包装器 */
.nav-item-wrapper {
  display: flex;
  flex-direction: column;
}

/* 导航项（包含线条+文字） */
.nav-item {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  cursor: pointer;
  position: relative;
  padding: 0.9375rem 0;
  z-index: 2;
}

/* 导航线条（默认+激活态） */
.nav-line {
  width: 1.25rem;
  height: 0.125rem;
  background-color: #ccc;
  transition: all 0.3s ease;
  position: relative;
  z-index: 2;
}

.nav-line.active {
  width: 2.5rem;
  background-color: #333;
}

.nav-text {
  font-size: 1rem;
  color: #666;
  transition: all 0.3s ease;
  position: relative;
  z-index: 2;
}

.nav-text.active {
  color: #000;
  font-size: 1.6rem;
}

/* 导航分隔线 */
.nav-divider {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1.875rem;
  padding: 0.3125rem 0;
  margin-left: 0;
}

.divider-line {
  width: 1.25rem;
  height: 0.0625rem;
}

.divider-line.darker {
  background-color: #999;
}

.divider-line.lighter {
  background-color: #e0e0e0;
}

/* 右侧内容区 */
.content {
  flex: 1;
  display: flex;
  padding: 2.5rem 0 0 0;
  gap: 4.5rem;
  align-items: flex-start;
}

/* 右侧图片 */
.content-img {
  height: 36.4rem;
  overflow: hidden;
  border-radius: 0.5rem;
  transition: all 0.3s ease;
  width: 56%;
}

.content-img img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
}

.content-img:hover img {
  transform: scale(1.05);
}

/* 轮播图容器 */
.carousel-container {
  flex: 1;
  height: 36.4rem;
  position: relative;
  overflow: hidden;
  border-radius: 0.5rem;
}

.carousel {
  position: relative;
  width: 100%;
  height: 100%;
}

.carousel-slide {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  transition: opacity 0.5s ease;
}

.carousel-slide.active {
  opacity: 1;
}

.carousel-slide img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
}

.carousel-slide.active img {
  transform: scale(1.05);
}

.slide-caption {
  display: none;
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  padding: 1rem;
  box-sizing: border-box;
  transition: all 0.3s ease;
}

.slide-caption h3 {
  margin: 0 0 0.5rem 0;
  font-size: 1.2rem;
}

.slide-caption p {
  margin: 0;
  font-size: 0.9rem;
}

/* 轮播图控制 */
.carousel-controls {
  position: absolute;
  bottom: 1rem;
  right: 1rem;
  display: flex;
  align-items: center;
  gap: 1rem;
}

.carousel-btn {
  background: rgba(255, 255, 255, 0.7);
  border: none;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.3s;
  font-size: 1.2rem;
  color: #333;
}

.carousel-btn:hover {
  background: rgba(255, 255, 255, 0.9);
}

.carousel-indicators {
  display: flex;
  gap: 0.5rem;
}

.carousel-indicators span {
  display: block;
  background: #83bd9966;
  border-radius: 3.5rem;
  cursor: pointer;
  height: 0.6rem;
  width: 1.6rem;
}

.carousel-indicators span.active {
  background: #83bd99;
}

/* 右侧文本 */
.content-text {
  flex: 1;
  font-size: 1.6rem;
  line-height: 2.4rem;
  color: #8d8d8d;
  transition: all 0.3s ease;
}

.content-text h2 {
  margin-bottom: 0.9375rem;
  font-size: 2rem;
  transition: all 0.3s ease;
  color: #000;
}

.content-text p {
  white-space: pre-line;
  margin: 0;
  font-size: 1.4rem;
}
.content-text p br {
  margin-top: 1.5rem;
}
/* 移动端卡片布局 */

/* 移动端卡片布局 */
.mobile-cards {
  display: none;
  flex-direction: column;
  gap: 2rem;
  margin-top: 2rem;
}

.mobile-card {
  border-radius: 0.5rem;
  overflow: hidden;
}

.card-header {
  padding: 1.25rem;
}

.card-title {
  margin: 0;
  font-size: 1.6rem;
  color: #333;
  font-weight: 600;
  margin-bottom: 0.8rem;
}

.card-content {
  padding: 0;
  display: flex;
  flex-direction: column;
}

/* 文字展开/收起功能 */
.text-collapsed {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.toggle-btn {
  background: none;
  border: none;
  color: #333;
  cursor: pointer;
  font-size: 0.875rem;
  margin-top: 1rem;
  padding: 0;
  text-decoration: underline;
  align-self: flex-start;
}

.toggle-btn:hover {
  color: #3a7d5c;
}

.card-text {
  padding: 1.5rem;
  order: 1;
  /* 确保文字在图片前面 */
  width: 100%;
  min-height: auto;
  flex-shrink: 0;
}

/* 修复移动端文字重叠问题 */
.card-text p {
  margin-bottom: 0.8rem;
  font-size: 1.4rem;
  line-height: 1.6;
  color: #333;
  word-wrap: break-word;
  hyphens: auto;
  overflow-wrap: break-word;
  clear: both;
  width: 100%;
  box-sizing: border-box;
}

.card-text p:last-child {
  margin-bottom: 0;
}

.collapsed-hidden {
  display: none;
}

.card-img {
  width: 100%;
  height: 40rem;
  overflow: hidden;
  order: 2;
}

.card-img img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.mobile-carousel {
  position: relative;
  height: 15rem;
  overflow: hidden;
  order: 2;
  /* 确保轮播图在文字后面 */
}

.mobile-carousel .carousel-slide {
  position: relative;
  opacity: 1;
  height: 100%;
}

.mobile-carousel-controls {
  position: absolute;
  bottom: 1rem;
  right: 1rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  z-index: 10;
}

/* 响应式设计 */
@media (max-width: 1200px) {
  .about-container {
    padding: 2rem 4rem;
  }
}

@media (max-width: 992px) {
  .about-container {
    padding: 1rem 2rem;
  }

  .sidebar,
  .content {
    display: none;
  }

  .mobile-cards {
    display: flex;
  }

  .about-container .head {
    flex-direction: column;
    gap: 1rem;
  }

  .about-container .head h1 {
    width: 100%;
    font-size: 2rem;
  }
}

@media (max-width: 768px) {
  .about-container {
    padding: 3rem;
  }
  .mobile-card {
    margin-bottom: 2rem;
  }
  .card-img,
  .mobile-carousel {
    height: 30rem;
    margin-bottom: 2rem;
  }

  .card-text {
    padding: 1.5rem;
    margin-bottom: 0;
  }

  .head-text {
    width: 100%;
  }
  .about-container .head h1 {
    font-size: 2.6rem;
  }
  .card-title {
    font-size: 1.8rem;
    font-weight: 500;
    color: #000;
  }
  .toggle-btn {
    font-size: 1.2rem;
  }
  
  /* 移动端特定修复 */
  .card-text p {
    line-height: 1.6 !important;
    margin-bottom: 1rem;
    clear: both;
    word-break: break-word;
  }
}

@media (max-width: 576px) {
  .mobile-card {
    margin-bottom: 2rem;
  }
  .toggle-btn {
    font-size: 1.2rem;
  }
  .card-img,
  .mobile-carousel {
    height: 30rem;
    margin-bottom: 2rem;
  }

  .card-header {
    padding: 0rem;
  }

  .card-text {
    padding: 0rem;
  }
  
  .card-text p {
    font-size: 1.3rem;
    line-height: 1.6;
    margin-bottom: 0.8rem;
  }

  .head-text {
    width: 100%;
  }

  .card-title {
    font-size: 1.6rem;
    font-weight: 500;
    color: #000;
  }

  .about-container .head {
    margin: 2rem 0 2rem 0;
  }
  .about-container .head h1 {
    font-size: 2.2rem;
  }
}

@media screen and (min-width: 1920px) {
  .about-container {
    padding: 6rem 10rem;
  }

  .content-img {
    height: 40rem;
  }
}
</style>
